/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Calendar } from 'lucide-react';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GestioneEventi() {
  const { user } = useAuth();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ titolo: '', descrizione: '', data_evento: '', ora_inizio: '', ora_fine: '', luogo: '', immagine_url: '', categoria: '', published: false });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events?published_only=false`, {});
      setEvents(res.data);
    } catch (err) { logger.error('Fetch error:', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm({ titolo: '', descrizione: '', data_evento: '', ora_inizio: '', ora_fine: '', luogo: '', immagine_url: '', categoria: '', published: false }); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e.id); setForm({ titolo: e.titolo, descrizione: e.descrizione || '', data_evento: e.data_evento, ora_inizio: e.ora_inizio || '', ora_fine: e.ora_fine || '', luogo: e.luogo || '', immagine_url: e.immagine_url || '', categoria: e.categoria || '', published: e.published }); setDialogOpen(true); };

  const handleSubmit = async () => {
    try {
      if (editing) { await axios.put(`${API}/events/${editing}`, form, {}); toast.success('Evento aggiornato'); }
      else { await axios.post(`${API}/events`, form, {}); toast.success('Evento creato'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => { try { await axios.delete(`${API}/events/${id}`, {}); toast.success('Evento eliminato'); fetchData(); } catch { toast.error('Errore'); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <PageTitle title="Gestione Eventi" info="Crea e gestisci gli eventi dello studio pubblicati sul sito." />
        </div>
        <Button onClick={openCreate} className="bg-[#238A92] hover:bg-[#0F5E66] text-white"><Plus size={16} className="mr-1" /> Nuovo Evento</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div> : events.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">Nessun evento. Crea il primo!</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Titolo</TableHead><TableHead>Data</TableHead><TableHead>Luogo</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {events.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.titolo}</TableCell>
                      <TableCell>{e.data_evento}</TableCell>
                      <TableCell>{e.luogo || '-'}</TableCell>
                      <TableCell><Badge className={e.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{e.published ? 'Pubblicato' : 'Bozza'}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openEdit(e)}><Edit size={14} /></Button>
                          <AlertDialog><AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="h-8 px-2 text-red-600"><Trash2 size={14} /></Button></AlertDialogTrigger>
                            <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Elimina Evento</AlertDialogTitle><AlertDialogDescription>Sei sicuro?</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(e.id)} className="bg-red-600">Elimina</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent></AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{fontFamily:'Space Grotesk'}}>{editing ? 'Modifica Evento' : 'Nuovo Evento'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Titolo</Label><Input value={form.titolo} onChange={e => setForm(p => ({...p, titolo: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Data</Label><Input type="date" value={form.data_evento} onChange={e => setForm(p => ({...p, data_evento: e.target.value}))} /></div>
              <div className="space-y-2"><Label>Categoria</Label><Input value={form.categoria} onChange={e => setForm(p => ({...p, categoria: e.target.value}))} placeholder="es. Congresso" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Ora Inizio</Label><Input type="time" value={form.ora_inizio} onChange={e => setForm(p => ({...p, ora_inizio: e.target.value}))} /></div>
              <div className="space-y-2"><Label>Ora Fine</Label><Input type="time" value={form.ora_fine} onChange={e => setForm(p => ({...p, ora_fine: e.target.value}))} /></div>
            </div>
            <div className="space-y-2"><Label>Luogo</Label><Input value={form.luogo} onChange={e => setForm(p => ({...p, luogo: e.target.value}))} placeholder="es. Studio Medico Boscovich" /></div>
            <div className="space-y-2"><Label>Descrizione</Label><Textarea value={form.descrizione} onChange={e => setForm(p => ({...p, descrizione: e.target.value}))} rows={4} /></div>
            <div className="space-y-2"><Label>URL Immagine</Label><Input value={form.immagine_url} onChange={e => setForm(p => ({...p, immagine_url: e.target.value}))} /></div>
            <div className="flex items-center gap-2">
              <Checkbox checked={form.published} onCheckedChange={v => setForm(p => ({...p, published: v}))} />
              <Label>Pubblicato (visibile sul sito)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSubmit} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">{editing ? 'Salva' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
